/* «Сменить голос в записи» и «Почистить запись» (APP-SPEC-5 §6; 03-api.md §9.2 `voice`): модель экрана без разметки.
   Режим задаёт вход (поле формы mode, вход студии options.mode или опубликованное поле voice_id), цена — из серверной единицы
   (app.price.unit === 'per_minute') или form.expected_price; ничего сверх ответа сервера модель не придумывает. */
import type { SpeechOrder } from '../../api/speech';
import { studioUnitPrice, type StudioApp, type StudioForm } from '../studio/studio-api';
import { toolDefault, toolField, type ToolField } from '../studio/circle/tool-form';
import { toolPrice, unitText } from '../studio/circle/tool-helpers';
import { tailDisplay } from '../studio/circle/order-tail-model';
import { uiFormat } from '../../i18n';

export type VoiceMode = 'other' | 'keep';
/** Способ отправки на итоге: голосовое Telegram или файл (§12.4) */
export type VoiceOut = 'voice' | 'file';
/** Предел записи по умолчанию, если политика поля его не публикует (§6: до 5:00) */
export const DEFAULT_MAX_MS = 300_000;
/** Штрихов волны — как у BotCore.wave.N */
export const WAVE_N = 52;

const rec = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
const str = (value: unknown) => (typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '');
const isMode = (value: string): value is VoiceMode => value === 'other' || value === 'keep';

/** Поле записи: upload с kind audio (`audio` шаблона или `audio_upload_id` контракта) */
export function audioField(form: StudioForm | null | undefined): ToolField | undefined {
  const field = toolField(form, 'audio', 'audio_upload_id');
  return field?.upload?.kind === 'audio' ? field : undefined;
}
/** Поле «Убрать шум»: `denoise` контракта (прежний ключ шаблона — `noise`) */
export const denoiseField = (form: StudioForm | null | undefined) => toolField(form, 'denoise', 'noise');
export const voiceField = (form: StudioForm | null | undefined) => toolField(form, 'voice_id');
export const modeField = (form: StudioForm | null | undefined) => toolField(form, 'mode');

/** Режим экрана: вход студии (options.mode) → значение поля mode формы → по опубликованному voice_id. */
export function voiceMode(form: StudioForm | null | undefined, initial?: Record<string, unknown>): VoiceMode {
  const field = modeField(form);
  const allowed = (value: string) => isMode(value) && (!field?.values.length || field.values.includes(value));
  const entry = str(initial?.mode);
  if (allowed(entry)) return entry as VoiceMode;
  const preset = toolDefault(field) ?? '';
  if (allowed(preset)) return preset as VoiceMode;
  if (field?.values.length === 1 && isMode(field.values[0])) return field.values[0];
  // Решает шаблон: voice_id в полях шаблона есть только у «Сменить голос». Контракт экрана (screen_fields)
  // публикует voice_id у обоих режимов, поэтому по нему режим не определить.
  return form?.fields?.some(item => item.key === 'voice_id') ? 'other' : 'keep';
}
/** Главный способ отправки по входу: options.out (voice | vm | file), иначе «Сменить голос» — файлом, «Почистить» — голосовым */
export function voiceOut(mode: VoiceMode, initial?: Record<string, unknown>, denoise = true): VoiceOut {
  const out = str(initial?.out);
  if (mode === 'keep' && !denoise) return 'voice';
  if (out === 'voice' || out === 'vm') return 'voice';
  if (out === 'file') return 'file';
  return mode === 'keep' ? 'voice' : 'file';
}
/** Значение «включено» поля-переключателя в его же кодировке */
export function onValue(field: ToolField | undefined, on: boolean) {
  const values = field?.values ?? [];
  const pick = on ? ['true', '1', 'on', 'yes'] : ['false', '0', 'off', 'no'];
  return values.find(value => pick.includes(value)) ?? (on ? 'true' : 'false');
}
export const isOn = (value: string | undefined) => ['true', '1', 'on', 'yes'].includes(String(value ?? '').toLowerCase());
/** «Убрать шум» по умолчанию: опубликованное значение поля, иначе по режиму (у «Почистить» включён, у «Сменить голос» — выключен) */
export function denoiseDefault(field: ToolField | undefined, mode: VoiceMode, initial?: Record<string, unknown>): string {
  const entry = str(initial?.noise ?? initial?.denoise);
  if (entry) return onValue(field, isOn(entry));
  if (mode === 'keep') return onValue(field, true);
  const preset = toolDefault(field);
  return preset !== undefined ? preset : onValue(field, false);
}

/** Цена за минуту: платная форма — прайс сервера (unit_price за секунду × 60), иначе серверная единица app.price (03-api.md §6.7) */
export function perMinute(app: StudioApp | null | undefined, form?: StudioForm | null): number | null {
  const second = studioUnitPrice(form, 'second');
  if (second !== null) return second * 60;
  const price = toolPrice(app?.price);
  return price.unit === 'per_minute' && price.amount !== null && price.amount > 0 ? price.amount : null;
}
/** Подзаголовок шапки «10 C в минуту»; нет серверной единицы — пусто */
export const voiceUnit = (app: StudioApp | null | undefined, form?: StudioForm | null) => unitText(perMinute(app, form), 'C в минуту');
/** Минуты записи: неполная — как полная, не меньше одной */
export const minutesOf = (ms: number) => Math.max(1, Math.ceil(ms / 60000));
/** Итог на кнопке: по цене минуты (оценка, сервер проверит expected_price) или expected_price формы; «от» — пока длина записи неизвестна */
export function voicePrice(app: StudioApp | null | undefined, form: StudioForm | null | undefined, durationMs: number | null) {
  const minute = perMinute(app, form);
  const known = durationMs !== null && durationMs > 0;
  /* прайс «за секунду»: сервер берёт целые секунды записи */
  const second = studioUnitPrice(form, 'second');
  if (second !== null) return { price: known ? Math.ceil(durationMs! / 1000) * second : minute, from: !known, estimate: known };
  if (minute !== null) return { price: known ? minutesOf(durationMs!) * minute : minute, from: !known, estimate: known };
  const fixed = form && Number.isFinite(form.expected_price) ? form.expected_price : null;
  return { price: fixed, from: !known && fixed !== null && fixed > 0, estimate: false };
}

export type VoiceMissing = 'rec' | 'file' | 'upload' | 'read' | 'long' | 'voice' | 's2s' | '';
/** Чего не хватает кнопке (порядок как missing() прототипа) */
export function voiceMissing(o: {
  recording: boolean;
  hasFile: boolean;
  uploaded: boolean;
  durationMs: number | null;
  maxMs: number;
  needsVoice: boolean;
  voice: { is_s2s: boolean } | null;
}): VoiceMissing {
  if (o.recording) return 'rec';
  if (!o.hasFile && !o.uploaded) return 'file';
  if (o.hasFile && !(o.durationMs && o.durationMs > 0)) return 'read';
  if (o.durationMs && o.durationMs > o.maxMs) return 'long';
  if (!o.uploaded) return 'upload';
  if (o.needsVoice && !o.voice) return 'voice';
  if (o.needsVoice && !o.voice!.is_s2s) return 's2s';
  return '';
}
export function missText(kind: VoiceMissing, maxMs: number) {
  return (
    {
      rec: 'Сначала остановите запись',
      file: 'Загрузите или запишите аудио',
      upload: 'Загружаем запись',
      read: 'Читаем запись, секунду',
      long: `Запись длиннее ${mmss(maxMs / 1000)}, загрузите короче`,
      voice: 'Выберите голос, которым зазвучит запись',
      s2s: 'Этот голос не умеет переозвучку, выберите другой',
      '': '',
    } as const
  )[kind];
}
/** Подпись кнопки: «Сменить голос», «Убрать шум», без шумоподавления у «Почистить» — «Сделать голосовым» */
export const actLabel = (mode: VoiceMode, denoise: boolean) => (mode === 'other' ? 'Сменить голос' : denoise ? 'Убрать шум' : 'Сделать голосовым');
export const modeTitle = (mode: VoiceMode) => (mode === 'other' ? 'Сменить голос в записи' : 'Почистить запись');

/** Тексты хвоста по режиму заказа (§6 «Хвост», BotRun.T прототипа) */
export function voiceTexts(mode: VoiceMode, denoise: boolean) {
  const keep = mode === 'keep';
  const tail = 'Пришлём в чат голосовым или файлом, как удобнее.';
  return {
    runH: keep ? (denoise ? 'Убираем шум' : 'Собираем запись') : 'Меняем голос',
    runP: 'Можно уйти с экрана: бот напишет, когда запись будет готова.',
    doneH: keep ? (denoise ? 'Шум убран' : 'Запись готова') : 'Голос изменён',
    doneP: `${keep ? (denoise ? 'Голос ваш, шума больше нет.' : 'Голос ваш, запись готова стать голосовым.') : 'Слова и интонация ваши, голос новый.'} ${tail}`,
    modH: 'Запись не прошла проверку',
    notMade: keep ? 'запись не собрана' : 'голос не изменён',
    techH: keep ? (denoise ? 'Не получилось убрать шум' : 'Не получилось собрать запись') : 'Не получилось сменить голос',
  };
}

/** Форматы доставки сервера → способ: голосовое (voice_note) или файл (file / audio / document) */
const VOICE_FORMATS = ['voice_note', 'voice', 'ogg'];
const FILE_FORMATS = ['file', 'audio', 'document', 'mp3'];
export function deliveryFormat(formats: string[], out: VoiceOut): string | null {
  const wanted = out === 'voice' ? VOICE_FORMATS : FILE_FORMATS;
  for (const name of wanted) if (formats.includes(name)) return name;
  return null;
}
export const sendLabel = (out: VoiceOut) => (out === 'voice' ? 'Отправить голосовым' : 'Отправить файлом');
export const sentLabel = (out: VoiceOut) => (out === 'voice' ? 'Голосовое в чате' : 'Файл в чате');
export const otherOut = (out: VoiceOut): VoiceOut => (out === 'voice' ? 'file' : 'voice');

/** Результат audio заказа (§9.4): длительность и волна — только из ответа сервера */
export function voiceResult(order: SpeechOrder | null | undefined): { durationMs: number | null; peaks: number[] | null; denoise: boolean | null; mode: VoiceMode | null; voiceName: string } {
  const result = rec(order?.result);
  const data = rec(result.data);
  const pick = (key: string) => (result[key] !== undefined ? result[key] : data[key]);
  const duration = Number(pick('duration_ms'));
  const raw = pick('peaks');
  const peaks = Array.isArray(raw) && raw.length >= 8 && raw.length <= 2000 && raw.every(value => Number.isFinite(value))
    ? normalizePeaks(raw.map(Number), WAVE_N)
    : null;
  const display = tailDisplay(order);
  const mode = str(display.mode);
  const noise = display.denoise ?? display.noise;
  return {
    durationMs: Number.isFinite(duration) && duration > 0 ? duration : null,
    peaks,
    denoise: typeof noise === 'boolean' ? noise : typeof noise === 'string' ? isOn(noise) : null,
    mode: isMode(mode) ? mode : null,
    voiceName: str(display.voice_name),
  };
}
/** Пики к n штрихам: максимум по корзинам, нормировка к 1, тишина — не ниже 0,07 (как BotCore.wave) */
export function normalizePeaks(values: number[], n = WAVE_N): number[] {
  if (!values.length) return [];
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const from = Math.floor((i * values.length) / n);
    const to = Math.max(from + 1, Math.floor(((i + 1) * values.length) / n));
    let peak = 0;
    for (let k = from; k < to && k < values.length; k++) peak = Math.max(peak, Math.abs(values[k]));
    out.push(peak);
  }
  const top = Math.max(...out) || 1;
  return out.map(value => Math.max(0.07, Math.min(1, value / top)));
}
/** Пики записи из файла в браузере (WebAudio): не демо — читается сам звук; не вышло — null */
export async function filePeaks(blob: Blob, n = WAVE_N, signal?: AbortSignal): Promise<{ peaks: number[]; durationMs: number } | null> {
  const Ctx = (globalThis as unknown as { OfflineAudioContext?: typeof OfflineAudioContext }).OfflineAudioContext;
  if (!Ctx) return null;
  try {
    const buffer = await blob.arrayBuffer();
    if (signal?.aborted) return null;
    const decoded = await new Ctx(1, 1, 8000).decodeAudioData(buffer);
    if (signal?.aborted || !Number.isFinite(decoded.duration) || decoded.duration <= 0) return null;
    const data = decoded.getChannelData(0);
    const step = Math.max(1, Math.floor(data.length / (n * 64)));
    const sampled: number[] = [];
    for (let i = 0; i < data.length; i += step) sampled.push(data[i]);
    return { peaks: normalizePeaks(sampled, n), durationMs: Math.round(decoded.duration * 1000) };
  } catch {
    return null;
  }
}
export function mmss(seconds: number) {
  const s = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
/** Размер файла как BotCore.fsize: «820 КБ», «4,4 МБ» */
export function fsize(bytes: number) {
  return bytes < 1048576
    ? `${Math.max(1, Math.round(bytes / 1024))} КБ`
    : `${(Math.round((bytes / 1048576) * 10) / 10).toLocaleString('ru-RU')} МБ`;
}
/** Ограничения рамки одной строкой: «MP3, WAV или M4A, до 5:00» (форматы и предел — из политики поля; «или» и «до» — словарём профиля) */
export function audioLimits(field: ToolField | undefined, maxMs: number) {
  const names: Record<string, string> = { 'audio/mpeg': 'MP3', 'audio/wav': 'WAV', 'audio/x-wav': 'WAV', 'audio/mp4': 'M4A', 'audio/x-m4a': 'M4A', 'audio/ogg': 'OGG', 'audio/webm': 'WebM' };
  const list = [...new Set((field?.upload?.accept ?? []).map(mime => names[mime] ?? mime.split('/')[1]?.toUpperCase()).filter(Boolean))];
  const kinds = list.length > 1 ? uiFormat('{0} или {1}', list.slice(0, -1).join(', '), list.at(-1)) : (list[0] ?? '');
  return [kinds, uiFormat('до {0}', mmss(maxMs / 1000))].filter(Boolean).join(', ');
}
/** Подпись голоса в строке «Голос ›»: «Мужской · Низкий · Мой голос»; ещё N голосов — только если сервер прислал число каталога */
export function voiceSub(voice: { tags: string[]; is_mine: boolean; is_s2s: boolean } | null, total?: number | null) {
  if (!voice) return 'Библиотека и ваши клоны';
  if (!voice.is_s2s) return 'Без переозвучки, выберите другой';
  const tags = voice.tags.slice(0, 2).join(' · ') || 'Голос';
  if (voice.is_mine) return `${tags} · Мой голос`;
  return total && total > 0 ? `${tags} · ещё ${total.toLocaleString('ru-RU')}+ голосов` : tags;
}
